import { AlertTriangle, ArrowRight, Inbox, Server, UserCheck, Users, UsersRound } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { AppMeta, DashboardData } from '../types'

export function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [meta, setMeta] = useState<AppMeta | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    void Promise.all([api<DashboardData>('/dashboard'), api<AppMeta>('/meta')])
      .then(([dashboard, appMeta]) => { setData(dashboard); setMeta(appMeta) })
      .catch((cause) => setError(cause instanceof Error ? cause.message : '加载失败'))
  }, [])

  if (error) return <ErrorState message={error} />
  if (!data) return <LoadingState />

  const cards = [
    { label: '员工邮箱', value: data.stats.employees, hint: `${data.stats.activeEmployees} 个正常使用`, icon: Users, tone: 'blue', to: '/employees' },
    { label: '群邮箱', value: data.stats.groups, hint: '团队邮件自动分发', icon: UsersRound, tone: 'violet', to: '/groups' },
    { label: '公共邮箱', value: data.stats.publicMailboxes, hint: '公共地址统一管理', icon: Inbox, tone: 'teal', to: '/public-mailboxes' },
    { label: '同步异常', value: data.stats.syncErrors, hint: data.stats.syncErrors ? '需要管理员处理' : '全部同步正常', icon: data.stats.syncErrors ? AlertTriangle : UserCheck, tone: data.stats.syncErrors ? 'amber' : 'green', to: '/audit' },
  ]

  return (
    <div className="page-stack">
      <section className="welcome-row">
        <div><p className="eyebrow">今日概览</p><h1>企业邮箱运行概况</h1><p>员工、群组和公共邮箱集中在一个中文后台完成管理。</p></div>
        <div className="engine-card"><div className="engine-icon"><Server size={22} /></div><div><span>Mox 邮件引擎</span><strong>{meta?.moxMode === 'live' ? '已连接生产服务' : '本地模拟模式'}</strong><small>{meta?.domain}</small></div><span className="state-pill"><i />正常</span></div>
      </section>
      {meta?.moxMode === 'mock' && <div className="info-banner"><AlertTriangle size={18} /><div><strong>当前为本地模拟模式</strong><span>所有操作只保存到本地数据库，不会修改线上 Mox 邮箱。</span></div></div>}
      <section className="stat-grid">
        {cards.map(({ label, value, hint, icon: Icon, tone, to }) => (
          <Link className="stat-card" to={to} key={label}>
            <div className={`stat-icon ${tone}`}><Icon size={22} /></div>
            <div><span>{label}</span><strong>{value}</strong><small>{hint}</small></div>
            <ArrowRight className="stat-arrow" size={18} />
          </Link>
        ))}
      </section>
      <section className="dashboard-grid">
        <article className="panel">
          <header className="panel-header"><div><h2>最近操作</h2><p>管理员对邮箱的关键变更</p></div><Link className="text-link" to="/audit">查看全部 <ArrowRight size={15} /></Link></header>
          <div className="activity-list">
            {data.recentAudit.length === 0 && <EmptyInline text="还没有操作记录" />}
            {data.recentAudit.map((item) => (
              <div className="activity-item" key={item.id}>
                <span className={`activity-dot ${item.result}`} />
                <div><strong>{item.action}</strong><p>{item.target}{item.detail ? ` · ${item.detail}` : ''}</p></div>
                <time>{formatTime(item.createdAt)}</time>
              </div>
            ))}
          </div>
        </article>
        <article className="panel quick-panel">
          <header className="panel-header"><div><h2>快速开始</h2><p>常用的邮箱管理操作</p></div></header>
          <Link className="quick-action" to="/employees"><span className="quick-icon blue"><Users size={20} /></span><div><strong>开通员工邮箱</strong><small>创建账号并生成初始密码</small></div><ArrowRight size={17} /></Link>
          <Link className="quick-action" to="/groups"><span className="quick-icon violet"><UsersRound size={20} /></span><div><strong>建立群邮箱</strong><small>一封邮件自动分发给成员</small></div><ArrowRight size={17} /></Link>
          <Link className="quick-action" to="/public-mailboxes"><span className="quick-icon teal"><Inbox size={20} /></span><div><strong>创建公共邮箱</strong><small>多人收件并使用公共地址发信</small></div><ArrowRight size={17} /></Link>
        </article>
      </section>
    </div>
  )
}

function LoadingState() { return <div className="loading-block"><span className="loader" />正在载入管理数据…</div> }
function ErrorState({ message }: { message: string }) { return <div className="error-state"><AlertTriangle size={26} /><strong>加载失败</strong><span>{message}</span></div> }
function EmptyInline({ text }: { text: string }) { return <div className="empty-inline">{text}</div> }
function formatTime(value: string) { return new Intl.DateTimeFormat('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(value)) }

